import {
  calcularAncoragem,
  CARGA_MAXIMA_ANCORAGEM_KGF,
  CARGA_MINIMA_ANCORAGEM_KGF,
  CONFIGURACAO_AVISOS_PADRAO,
  DESVIO_MAXIMO_CARGA_GRAUS,
  pontosDeAncoragem,
  PONTOS_DE_FIXACAO,
  TIPOS_ANCORAGEM,
  type AncoragemTabletop,
  type AtualizacoesAncoragem,
  type CenaTabletop,
  type EqualizacaoAncoragem,
  type ConfiguracaoAvisos,
  type ReferenciaPonto,
  type ResultadoAncoragem,
} from '../../dominio/tabletop'
import { SimulacaoAncoragemV } from './PainelSimulacoes'
import type { DesenhoAncoragem } from './useAncoragensTabletop'

function nomeDoPonto(cena: CenaTabletop, referencia: ReferenciaPonto): string {
  const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
  if (!objeto) return `${referencia.idObjeto} · ${referencia.idPonto}`
  const ponto = pontosDeAncoragem(objeto).find((p) => p.id === referencia.idPonto)
  return `${objeto.nome} · ${ponto?.nome ?? `${referencia.idPonto} (não existe mais)`}`
}

const DESCRICAO_TIPO = { V: 'duas pernas', W: 'três pernas', M: 'quatro pernas' } as const

export const nomeDaAncoragem = (a: AncoragemTabletop) => a.rotulo ?? `Ancoragem em ${a.tipo}`

const inteiro = (valor: number) => String(Math.round(valor))

/** O resumo de uma linha: abertura e carga por perna, ou o motivo de não haver. */
export function resumoDaAncoragem(r: ResultadoAncoragem): string {
  if (r.incompleta) return 'incompleta: algum ponto não existe mais'
  if (r.degenerada) return `${inteiro(r.anguloMaximoGraus!)}° · pernas quase opostas`
  return r.equalizacao === 'pre-equalizada'
    ? `${inteiro(r.anguloMaximoGraus!)}° · pré-equalizada, até ${inteiro(r.fracaoPorPerna! * 100)}% numa perna`
    : `${inteiro(r.anguloMaximoGraus!)}° · ${inteiro(r.fracaoPorPerna! * 100)}% da carga por perna`
}

const ROTULOS_EQUALIZACAO: Readonly<Record<EqualizacaoAncoragem, string>> = {
  autoequalizavel: 'Autoequalizável (a fita corre no mosquetão)',
  'pre-equalizada': 'Pré-equalizada (nó no ponto central)',
}

/** O que acontece se cada ponto falhar, numa frase por ponto. */
function textoDaFalha(falha: ResultadoAncoragem['seUmPontoFalhar'][number], nome: (i: number) => string): string {
  const perna = `Se o ponto ${falha.perna + 1} (${nome(falha.perna)}) falhar`
  if (!falha.fracoes) return `${perna}, nenhuma perna segura a carga.`
  const restantes = falha.fracoes
    .map((f, i) => ({ f, i }))
    .filter(({ i }) => i !== falha.perna)
    .map(({ f, i }) => `perna ${i + 1} com ${inteiro(f * 100)}%`)
    .join(', ')
  if (falha.comExtensao) {
    return `${perna}: a fita corre no mosquetão e a carga desce até as outras pernas esticarem (extensão), depois fica ${restantes}.`
  }
  return falha.balanca
    ? `${perna}: sem extensão, mas o ponto central balança até se alinhar; depois fica ${restantes}.`
    : `${perna}: sem extensão, a carga passa para ${restantes}.`
}

export interface PropsPainelAncoragens {
  cena: CenaTabletop
  desenho: DesenhoAncoragem | null
  ancoragemSelecionada: AncoragemTabletop | null
  configuracao?: ConfiguracaoAvisos
  aoIniciar: (tipo: 'V' | 'W' | 'M') => void
  aoDesfazerPonto: () => void
  aoCancelar: () => void
  aoSelecionar: (id: string | null) => void
  aoAtualizar: (id: string, alteracoes: AtualizacoesAncoragem) => void
  aoRemover: (id: string) => void
  aoGerarCentroAutomatico?: () => void
}

/**
 * Ancoragem em V, W ou M: escolher o tipo, tocar nos pontos de fixação e no
 * ponto central, e ler o ângulo entre as pernas e a carga por perna. O cálculo
 * é didático (pernas equalizadas, sem atrito) e isso fica escrito na tela.
 */
export function PainelAncoragens(props: PropsPainelAncoragens) {
  const { cena, desenho, ancoragemSelecionada } = props
  const ancoragens = cena.ancoragens ?? []
  const configuracao = props.configuracao ?? CONFIGURACAO_AVISOS_PADRAO

  return (
    <div className="grupo-controle painel-ancoragens">
      <span className="titulo-controle">Ancoragem em V, W ou M</span>

      {desenho ? (
        <div role="group" aria-label={`Desenhando ancoragem em ${desenho.tipo}`}>
          <p className="dica-tabletop" aria-live="polite">
            {desenho.fixacoes.length < PONTOS_DE_FIXACAO[desenho.tipo]
              ? `Ancoragem em ${desenho.tipo}: toque no ponto de fixação ${desenho.fixacoes.length + 1} de ${PONTOS_DE_FIXACAO[desenho.tipo]}.`
              : 'Agora toque no ponto central, onde a carga se pendura (ou gere automaticamente).'}
          </p>
          {desenho.fixacoes.length > 0 && (
            <ol className="lista-pontos-ancoragem corda-percurso">
              {desenho.fixacoes.map((ref, i) => (
                <li key={i}>Fixação: {nomeDoPonto(cena, ref)}</li>
              ))}
            </ol>
          )}
          <div className="acoes-linha">
            {desenho.fixacoes.length >= PONTOS_DE_FIXACAO[desenho.tipo] && props.aoGerarCentroAutomatico && (
              <button
                type="button"
                className="btn-acao-cad btn-acao-cad--destaque"
                onClick={props.aoGerarCentroAutomatico}
              >
                Gerar ponto central
              </button>
            )}
            <button type="button" className="btn-acao-cad btn-acao-cad--secundario" disabled={desenho.fixacoes.length === 0} onClick={props.aoDesfazerPonto}>
              Desfazer ponto
            </button>
            <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={props.aoCancelar}>
              Cancelar (Esc)
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="dica-tabletop">Escolha o tipo, toque nos pontos de fixação e depois no ponto central.</p>
          <div className="funcoes-corda" role="group" aria-label="Montar ancoragem por tipo">
            {TIPOS_ANCORAGEM.map((tipo) => (
              <button key={tipo} type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={() => props.aoIniciar(tipo)}>
                {tipo} <span className="dica">({PONTOS_DE_FIXACAO[tipo]} pontos, {DESCRICAO_TIPO[tipo]})</span>
              </button>
            ))}
          </div>
        </>
      )}

      {ancoragemSelecionada && !desenho && (
        <InspetorAncoragemVWM {...props} ancoragem={ancoragemSelecionada} configuracao={configuracao} />
      )}

      <details open={ancoragens.length > 0 && !ancoragemSelecionada}>
        <summary>Ancoragens na cena ({ancoragens.length})</summary>
        {ancoragens.length === 0 ? (
          <p className="dica">Nenhuma ancoragem ainda.</p>
        ) : (
          <ul className="lista-pontos-ancoragem lista-cordas">
            {ancoragens.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  className="btn-link-corda"
                  aria-pressed={a.id === ancoragemSelecionada?.id}
                  onClick={() => props.aoSelecionar(a.id === ancoragemSelecionada?.id ? null : a.id)}
                >
                  {nomeDaAncoragem(a)} <span className="dica">({resumoDaAncoragem(calcularAncoragem(cena, a))})</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </details>
    </div>
  )
}

function InspetorAncoragemVWM(props: PropsPainelAncoragens & { ancoragem: AncoragemTabletop; configuracao: ConfiguracaoAvisos }) {
  const { cena, ancoragem, configuracao } = props
  const r = calcularAncoragem(cena, ancoragem)
  const acima = !r.incompleta && !r.degenerada && r.anguloMaximoGraus! > configuracao.anguloMaximoAncoragemGraus

  return (
    <fieldset className="inspetor-vitima inspetor-corda">
      <legend>Ancoragem selecionada</legend>
      <p className="corda-resumo">
        <strong>{nomeDaAncoragem(ancoragem)}</strong>
        <span className="dica"> · {resumoDaAncoragem(r)}</span>
      </p>

      <label className="campo">
        <span>rótulo</span>
        <input
          value={ancoragem.rotulo ?? ''}
          placeholder="opcional"
          aria-label="Rótulo da ancoragem"
          onChange={(e) => props.aoAtualizar(ancoragem.id, { rotulo: e.target.value })}
        />
      </label>

      <div className="campo">
        <span>carga no ponto central (kgf)</span>
        <input
          type="range"
          aria-label="Carga, controle deslizante"
          min={CARGA_MINIMA_ANCORAGEM_KGF}
          max={CARGA_MAXIMA_ANCORAGEM_KGF}
          step={10}
          value={ancoragem.cargaKgf}
          onChange={(e) => props.aoAtualizar(ancoragem.id, { cargaKgf: Number(e.target.value) })}
        />
        <input
          type="number"
          aria-label="Carga, valor"
          min={CARGA_MINIMA_ANCORAGEM_KGF}
          max={CARGA_MAXIMA_ANCORAGEM_KGF}
          step={10}
          value={ancoragem.cargaKgf}
          onChange={(e) => {
            const numero = Number(e.target.value)
            if (e.target.value !== '' && Number.isFinite(numero)) props.aoAtualizar(ancoragem.id, { cargaKgf: numero })
          }}
        />
      </div>

      <label className="campo">
        <span>equalização</span>
        <select
          value={r.equalizacao}
          aria-label="Equalização da ancoragem"
          onChange={(e) => props.aoAtualizar(ancoragem.id, { equalizacao: e.target.value as EqualizacaoAncoragem })}
        >
          {(Object.keys(ROTULOS_EQUALIZACAO) as EqualizacaoAncoragem[]).map((chave) => (
            <option key={chave} value={chave}>
              {ROTULOS_EQUALIZACAO[chave]}
            </option>
          ))}
        </select>
      </label>

      <label className="campo">
        <span>
          desvio da carga: {inteiro(ancoragem.desvioCargaGraus ?? 0)}° do eixo da ancoragem
          {ancoragem.desvioCargaGraus ? ` (para o lado do ponto ${ancoragem.desvioCargaGraus > 0 ? ancoragem.fixacoes.length : 1})` : ''}
        </span>
        <input
          type="range"
          aria-label="Direção da carga"
          min={-DESVIO_MAXIMO_CARGA_GRAUS}
          max={DESVIO_MAXIMO_CARGA_GRAUS}
          step={5}
          value={ancoragem.desvioCargaGraus ?? 0}
          onChange={(e) => props.aoAtualizar(ancoragem.id, { desvioCargaGraus: Number(e.target.value) })}
        />
      </label>

      <p className="corda-resumo" aria-live="polite">
        {r.incompleta
          ? 'Algum ponto desta ancoragem não existe mais (o objeto mudou de forma): apague e monte de novo.'
          : r.degenerada
            ? `Pernas quase opostas (${inteiro(r.anguloMaximoGraus!)}°): a carga por perna dispara. Aproxime os pontos de fixação.`
            : r.equalizacao === 'pre-equalizada'
              ? `A perna mais carregada recebe cerca de ${inteiro(r.cargaPorPernaKgf!)} kgf (${inteiro(r.fracaoPorPerna! * 100)}% da carga).`
              : `Cada perna recebe cerca de ${inteiro(r.cargaPorPernaKgf!)} kgf (${inteiro(r.fracaoPorPerna! * 100)}% da carga).`}
      </p>
      {r.cargaDesalinhada && (
        <p className="aviso-corda" role="note">
          A carga sai da direção que as pernas seguram: numa pré-equalizada o ponto central balança até se alinhar, e uma perna pode ficar frouxa.
        </p>
      )}

      {acima && (
        <p className="aviso-corda" role="alert">
          A abertura de {inteiro(r.anguloMaximoGraus!)}° passa do limite de {inteiro(configuracao.anguloMaximoAncoragemGraus)}°.
        </p>
      )}

      <table className="tabela-pernas">
        <caption className="dica">Pernas, do ponto de fixação ao ponto central</caption>
        <thead>
          <tr>
            <th scope="col">Perna</th>
            <th scope="col">Ponto de fixação</th>
            <th scope="col">Comprimento</th>
            <th scope="col">Carga</th>
          </tr>
        </thead>
        <tbody>
          {r.pernas.map((perna, i) => (
            <tr key={i}>
              <th scope="row">{i + 1}</th>
              <td>{nomeDoPonto(cena, perna.referencia)}</td>
              <td>{perna.comprimentoM === null ? '—' : `${perna.comprimentoM.toFixed(2).replace('.', ',')} m`}</td>
              <td>{r.cargasPorPernaKgf ? `${inteiro(r.cargasPorPernaKgf[i]!)} kgf (${inteiro(r.fracoesPorPerna![i]! * 100)}%)` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {r.angulosEntrePernas.length > 0 && (
        <p className="dica">
          Ângulo entre pernas:{' '}
          {r.angulosEntrePernas.map((a) => `${a.de + 1}–${a.para + 1}: ${inteiro(a.graus)}°`).join(' · ')}
        </p>
      )}
      {r.seUmPontoFalhar.length > 0 && (
        <ul className="lista-falhas-ancoragem" aria-label="Se um ponto falhar">
          {r.seUmPontoFalhar.map((f) => (
            <li key={f.perna}>{textoDaFalha(f, (i) => nomeDoPonto(cena, r.pernas[i]!.referencia))}</li>
          ))}
        </ul>
      )}
      <p className="dica">
        {r.equalizacao === 'pre-equalizada'
          ? 'Cálculo didático da pré-equalizada: as pernas esticam como molas do mesmo material (a mais curta é a mais dura), sem atrito. Mude a direção da carga para ver uma perna carregar mais.'
          : 'Cálculo didático da autoequalizável: a fita corre sem atrito, as pernas ficam com a mesma tensão e o ponto central acompanha a direção da carga.'}{' '}
        Não vale como capacidade de equipamento.
      </p>
      <p className="dica">Ponto central: {nomeDoPonto(cena, ancoragem.centro)}</p>

      {ancoragem.tipo === 'V' && !r.incompleta && <SimulacaoAncoragemV cena={cena} ancoragem={ancoragem} />}

      <div className="acoes-linha">
        <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={() => props.aoSelecionar(null)}>
          Fechar
        </button>
        <button type="button" className="btn-acao-cad btn-acao-cad--perigo" onClick={() => props.aoRemover(ancoragem.id)}>
          Apagar ancoragem
        </button>
      </div>
    </fieldset>
  )
}
